import React, { useState, useMemo } from "react";
import { mockCreator } from "../../../data/mockData";
import BarChart from "../components/BarChart";
import LineChart from "../components/LineChart";
import Card from "../../../components/ui/Card";
import Tabs from "../../../components/ui/Tabs";
import TopProductsWidget from "../components/analytics/TopProductsWidget";
import DateRangeFilter from "../components/analytics/DateRangeFilter";
import { ChevronDown, TrendingUp, Users, DollarSign, Activity, Share2, Smartphone, Monitor, Tablet } from "lucide-react";

const AnalyticsPage = () => {
  const [period, setPeriod] = useState("30d");
  const { analytics } = mockCreator;

  // Mock data reactivity based on period
  const filteredAnalytics = useMemo(() => {
    // For a real app, this would fetch new data. 
    // For the prototype, we'll just vary the data slightly to show it's "working"
    if (period === "7d") {
      return {
        ...analytics,
        followerGrowth: analytics.followerGrowth.slice(-2),
        revenueByMonth: analytics.revenueByMonth.slice(-2),
      };
    }
    if (period === "yearly") {
      return {
        ...analytics,
        followerGrowth: analytics.followerGrowth.map(item => ({ ...item, value: item.value * 12 })),
        revenueByMonth: analytics.revenueByMonth.map(item => ({ ...item, value: item.value * 12 })),
      };
    }
    if (period === "custom") {
        // Just show a subset to simulate a custom range
        return {
          ...analytics,
          followerGrowth: analytics.followerGrowth.slice(1, 4),
          revenueByMonth: analytics.revenueByMonth.slice(1, 4),
        };
    }
    return analytics;
  }, [period, analytics]);

  const OverviewTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 @lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
           <div className="flex justify-between items-start mb-2">
             <div className="p-2 bg-green-50 text-green-600 rounded-lg"><DollarSign size={18} /></div>
             <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">+12.5%</span>
           </div>
           <div className="text-xl @sm:text-2xl font-bold text-slate-800">45.2k</div>
           <div className="text-[10px] @sm:text-sm text-slate-500 truncate">Total Revenue</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
           <div className="flex justify-between items-start mb-2">
             <div className="p-2 bg-blue-50 text-blue-600 rounded-lg"><Users size={18} /></div>
             <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">+854</span>
           </div>
           <div className="text-xl @sm:text-2xl font-bold text-slate-800">12.4k</div>
           <div className="text-[10px] @sm:text-sm text-slate-500 truncate">Followers</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
           <div className="flex justify-between items-start mb-2">
             <div className="p-2 bg-purple-50 text-purple-600 rounded-lg"><Activity size={18} /></div>
             <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">+5.2%</span>
           </div>
           <div className="text-xl @sm:text-2xl font-bold text-slate-800">8.4%</div>
           <div className="text-[10px] @sm:text-sm text-slate-500 truncate">Engagement</div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
           <div className="flex justify-between items-start mb-2">
             <div className="p-2 bg-orange-50 text-orange-600 rounded-lg"><TrendingUp size={18} /></div>
             <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">+128</span>
           </div>
           <div className="text-xl @sm:text-2xl font-bold text-slate-800">1.2k</div>
           <div className="text-[10px] @sm:text-sm text-slate-500 truncate">Daily Visits</div>
        </div>
      </div>

      <div className="grid grid-cols-1 @lg:grid-cols-2 gap-6">
          <LineChart data={filteredAnalytics.followerGrowth} title="Follower Growth" />
          <BarChart data={filteredAnalytics.revenueByMonth} title="Monthly Revenue (ETB)" />
      </div>

      <div className="flex flex-col @lg:grid @lg:grid-cols-3 gap-6">
        <div className="@lg:col-span-2">
           <Card>
            <h3 className="text-lg font-bold text-slate-800 mb-4">Top Performing Content</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-100">
                    <th className="text-left font-medium pb-4">Content</th>
                    <th className="text-right font-medium pb-4">Views</th>
                    <th className="text-right font-medium pb-4">Engagement</th>
                    <th className="text-right font-medium pb-4">Shares</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAnalytics.topPosts.map((post) => (
                    <tr key={post.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-4 font-medium text-slate-700">{post.title}</td>
                      <td className="py-4 text-right text-slate-600">{post.views.toLocaleString()}</td>
                      <td className="py-4 text-right text-emerald-600 font-bold">{post.engagement}</td>
                      <td className="py-4 text-right text-slate-600">{post.shares || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
           </Card>
        </div>
        <div>
            <TopProductsWidget />
        </div>
      </div>
    </div>
  );

  const AudienceTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 @lg:grid-cols-2 gap-6">
        <Card>
            <h3 className="text-lg font-bold text-slate-800 mb-6">Demographics</h3>
            <div className="space-y-8">
              <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Age Distribution</h4>
                  <div className="space-y-4">
                      {filteredAnalytics.audienceAge.map((item, idx) => (
                        <div key={idx}>
                           <div className="flex justify-between text-sm mb-1">
                             <span className="text-slate-700 font-medium">{item.label}</span>
                             <span className="text-slate-500">{item.value}%</span>
                           </div>
                           <div className="w-full bg-slate-100 rounded-full h-2">
                             <div className="bg-primary h-2 rounded-full" style={{width: `${item.value}%`}}></div>
                           </div>
                        </div>
                      ))}
                  </div>
              </div>

              <div className="pt-6 border-t border-slate-100">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Gender</h4>
                  <div className="flex items-center gap-6">
                    <div className="flex-1">
                       <div className="flex justify-between text-sm mb-1">
                         <span className="text-slate-700 font-medium font-medium">Female</span>
                         <span className="text-slate-500">55%</span>
                       </div>
                       <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden flex">
                          <div className="bg-pink-400 h-full" style={{width: '55%'}}></div>
                          <div className="bg-blue-400 h-full" style={{width: '45%'}}></div>
                       </div>
                       <div className="flex justify-between text-sm mt-1">
                         <span className="text-xs text-slate-400">45% Male</span>
                       </div>
                    </div>
                  </div>
              </div>
            </div>
        </Card>

        <Card>
            <h3 className="text-lg font-bold text-slate-800 mb-6">Device & Location</h3>
            <div className="space-y-8">
               <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Device Usage</h4>
                  <div className="flex justify-around py-4">
                     <div className="text-center">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                          <Smartphone size={20} />
                        </div>
                        <div className="text-sm font-bold text-slate-800">82%</div>
                        <div className="text-[10px] text-slate-400">Mobile</div>
                     </div>
                     <div className="text-center">
                        <div className="p-3 bg-slate-50 text-slate-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                          <Monitor size={20} />
                        </div>
                        <div className="text-sm font-bold text-slate-800">15%</div>
                        <div className="text-[10px] text-slate-400">Desktop</div>
                     </div>
                     <div className="text-center">
                        <div className="p-3 bg-slate-50 text-slate-600 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                          <Tablet size={20} />
                        </div>
                        <div className="text-sm font-bold text-slate-800">3%</div>
                        <div className="text-[10px] text-slate-400">Tablet</div>
                     </div>
                  </div>
               </div>

               <div className="pt-6 border-t border-slate-100">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Top Locations</h4>
                  <ul className="space-y-3">
                      {[{city: "Addis Ababa", p: "68%"}, {city: "Dire Dawa", p: "12%"}, {city: "Bahir Dar", p: "8%"}, {city: "International", p: "12%"}].map((loc, idx) => (
                         <li key={idx} className="flex items-center justify-between text-sm">
                            <span className="text-slate-600">{loc.city}</span>
                            <span className="font-bold text-slate-800">{loc.p}</span>
                         </li>
                      ))}
                  </ul>
               </div>
            </div>
        </Card>
      </div>
    </div>
  );

  const RevenueTab = () => (
    <div className="space-y-6">
       <div className="grid grid-cols-1 @lg:grid-cols-3 gap-6">
          <div className="@lg:col-span-2">
             <BarChart data={filteredAnalytics.revenueByMonth} title="Revenue Trend" />
          </div>
          <Card>
             <h3 className="text-lg font-bold text-slate-800 mb-6">Earnings Breakdown</h3>
             <div className="space-y-6">
                {filteredAnalytics.revenueSources.map((source, idx) => (
                   <div key={idx}>
                      <div className="flex justify-between text-sm mb-2">
                         <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{backgroundColor: source.color}}></div>
                            <span className="text-slate-600">{source.label}</span>
                         </div>
                         <span className="font-bold text-slate-800">{source.value.toLocaleString()} ETB</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                         <div 
                           className="h-1.5 rounded-full" 
                           style={{
                             width: `${(source.value / filteredAnalytics.revenueSources.reduce((a,b) => a + b.value, 0)) * 100}%`,
                             backgroundColor: source.color
                           }}
                         />
                      </div>
                   </div>
                ))}
                <div className="pt-4 border-t border-slate-100">
                   <div className="flex justify-between items-center text-slate-800">
                      <span className="font-medium">Total Balance</span>
                      <span className="text-xl font-bold">45,750 ETB</span>
                   </div>
                </div>
             </div>
          </Card>
       </div>

       <Card>
          <div className="flex justify-between items-center mb-6">
             <h3 className="text-lg font-bold text-slate-800">Recent Transactions</h3>
             <button className="text-primary text-sm font-bold hover:underline">View All</button>
          </div>
          <div className="overflow-x-auto">
             <table className="min-w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-medium">
                   <tr>
                      <th className="p-3 rounded-l-lg font-medium">Date</th>
                      <th className="p-3 font-medium">Description</th>
                      <th className="p-3 font-medium">Type</th>
                      <th className="p-3 rounded-r-lg text-right font-medium">Amount</th>
                   </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                   {[
                     { date: "Oct 12, 2025", desc: "Subscription: Fan Club", type: "Digital", amt: "+75.00" },
                     { date: "Oct 11, 2025", desc: "Sale: Signature Hoodie", type: "Physical", amt: "+1,800.00" },
                     { date: "Oct 10, 2025", desc: "Tip from fan_01", type: "Tip", amt: "+500.00" },
                     { date: "Oct 09, 2025", desc: "Ad Revenue (Awash Bank)", type: "Ad", amt: "+12,400.00" },
                   ].map((tx, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                         <td className="p-3 text-slate-500">{tx.date}</td>
                         <td className="p-3 text-slate-800 font-medium">{tx.desc}</td>
                         <td className="p-3">
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px] font-bold uppercase">{tx.type}</span>
                         </td>
                         <td className="p-3 text-right text-emerald-600 font-bold">{tx.amt} ETB</td>
                      </tr>
                   ))}
                </tbody>
             </table>
          </div>
       </Card>
    </div>
  );

  const tabs = [
    { name: "Overview", content: <OverviewTab /> },
    { name: "Audience", content: <AudienceTab /> },
    { name: "Revenue", content: <RevenueTab /> },
  ];

  return (
    <div className="@container">
      <div className="flex flex-col @md:flex-row items-start @md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl @md:text-3xl font-bold text-slate-800">Analytics</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Track your performance and audience growth.
          </p>
        </div>
        <DateRangeFilter onRangeChange={(val) => setPeriod(val)} />
      </div>

      <Tabs tabs={tabs} />
    </div>
  );
};

export default AnalyticsPage;
